'use client';

import { useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';

interface NewsletterSubscribeFormProps {
  /** Provenance tag — which widget/page submitted. */
  source: string;
  /** Visual variant. `card` = generous padded surface, `compact` = inline. */
  variant?: 'card' | 'compact';
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The four audience-style language choices (doc 78 §Go-live). A widget has no
 * country field, so the two Hungarian audiences (Hungary vs Romania) can only
 * be told apart by what the subscriber picks here — hence each choice carries
 * the `language` + (where it matters) `country` that the server feeds to
 * `resolveAudience`. Native language names, identical across UI locales.
 */
const LANGUAGE_CHOICES = [
  { value: 'ro', label: 'Română', language: 'ro' as const, country: null },
  { value: 'hu-ro', label: 'Magyar (Románia)', language: 'hu' as const, country: 'RO' },
  { value: 'hu-hu', label: 'Magyar (Magyarország)', language: 'hu' as const, country: 'HU' },
  { value: 'en', label: 'English', language: 'en' as const, country: null },
] as const;

/** Sensible default choice from the page locale. HU defaults to the Romania
 *  audience — the hotel sits in Székely-land, so HU readers skew local. */
function defaultChoiceFor(locale: string): string {
  if (locale === 'ro') return 'ro';
  if (locale === 'hu') return 'hu-ro';
  return 'en';
}

export function NewsletterSubscribeForm({ source, variant = 'card' }: NewsletterSubscribeFormProps) {
  const t = useTranslations('newsletterPage');
  const locale = useLocale();
  const [email, setEmail] = useState('');
  const [choice, setChoice] = useState<string>(() => defaultChoiceFor(locale));
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error' | 'invalid'>('idle');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_RE.test(trimmed)) {
      setStatus('invalid');
      return;
    }
    setStatus('submitting');
    const selected = LANGUAGE_CHOICES.find((c) => c.value === choice) ?? LANGUAGE_CHOICES[3];
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmed,
          locale,
          source,
          language: selected.language,
          country: selected.country,
        }),
      });
      if (!res.ok) {
        setStatus('error');
        return;
      }
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }
  }

  if (status === 'success') {
    return (
      <div className={variant === 'card' ? 'rounded-xl border-2 border-[#801515]/20 bg-[#faf8f6] p-6 text-center' : 'text-center'}>
        <h3 className="font-poppins text-2xl font-semibold text-[#801515] mb-2">{t('successTitle')}</h3>
        <p className="text-gray-700">{t('successBody')}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <label className="block">
        <span className="block text-sm font-medium text-gray-700 mb-1.5">{t('languageLabel')}</span>
        <select
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          disabled={status === 'submitting'}
          className="w-full px-6 py-4 bg-white border-2 border-gray-200 rounded-xl text-gray-900 focus:border-[#801515] focus:outline-none focus:ring-2 focus:ring-[#801515]/20 transition-all duration-300 text-lg disabled:opacity-60"
        >
          {LANGUAGE_CHOICES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="sr-only">{t('emailLabel')}</span>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status === 'invalid' || status === 'error') setStatus('idle');
          }}
          placeholder={t('emailPlaceholder')}
          disabled={status === 'submitting'}
          className="w-full px-6 py-4 bg-white border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:border-[#801515] focus:outline-none focus:ring-2 focus:ring-[#801515]/20 transition-all duration-300 text-lg disabled:opacity-60"
        />
      </label>
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full px-8 py-4 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30 transform hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
      >
        {status === 'submitting' ? t('submitting') : t('submit')}
      </button>
      {status === 'invalid' && (
        <p className="text-sm text-red-700">{t('errorInvalid')}</p>
      )}
      {status === 'error' && (
        <div className="text-sm text-red-700">
          <p className="font-semibold">{t('errorTitle')}</p>
          <p>{t('errorBody')}</p>
        </div>
      )}
      <p className="text-xs text-gray-500 text-center">{t('privacyNote')}</p>
    </form>
  );
}
